import type { GraphPoint } from '@/frontend/features/Fde/useOntologyGraphLayout'

export interface LabelSize { width: number; height: number }

interface LabelEdge { id: string; points: GraphPoint[]; size: LabelSize }
interface LabelNode extends GraphPoint { width: number; height: number }
interface Rect { left: number; right: number; top: number; bottom: number }

export interface EdgeLabelPlacement extends GraphPoint {
  anchor: GraphPoint
}

export function edgeLabelSize(label: string): LabelSize {
  return { width: Math.min(180, Math.max(62, label.length * 5.4 + 22)), height: 22 }
}

export function edgeLabelText(label: string, cardinality?: string | null) {
  const text = [label, cardinality].filter(Boolean).join(' · ')
  return text.length > 32 ? `${text.slice(0, 31)}…` : text
}

export function edgeLabelPlacement(points: GraphPoint[], size: LabelSize): EdgeLabelPlacement | null {
  if (points.length < 2) return null
  const segments = points.slice(1).map((to, index) => {
    const from = points[index]
    const dx = to.x - from.x
    const dy = to.y - from.y
    return { from, to, dx, dy, length: Math.hypot(dx, dy), internal: index > 0 && index < points.length - 2 }
  }).filter((segment) => segment.length > 0)
  const segment = segments.sort((left, right) => (
    (right.length + (right.internal ? 32 : 0)) - (left.length + (left.internal ? 32 : 0))
  ))[0]
  if (!segment) return null

  const anchor = { x: (segment.from.x + segment.to.x) / 2, y: (segment.from.y + segment.to.y) / 2 }
  const horizontal = Math.abs(segment.dx) >= Math.abs(segment.dy)
  return horizontal
    ? { x: anchor.x, y: anchor.y - size.height / 2 - 8, anchor }
    : { x: anchor.x + size.width / 2 + 8, y: anchor.y, anchor }
}

export function offsetEdgeLabel(center: GraphPoint, direction: GraphPoint, size: LabelSize): GraphPoint {
  const horizontal = Math.abs(direction.x) >= Math.abs(direction.y)
  return horizontal
    ? { x: center.x, y: center.y - size.height / 2 - 8 }
    : { x: center.x + size.width / 2 + 8, y: center.y }
}

function placementCandidates(points: GraphPoint[], size: LabelSize) {
  return points.slice(1).flatMap((to, index) => {
    const from = points[index]
    const dx = to.x - from.x
    const dy = to.y - from.y
    const length = Math.hypot(dx, dy)
    if (length === 0) return []
    const horizontal = Math.abs(dx) >= Math.abs(dy)
    const baseOffset = horizontal ? size.height / 2 + 8 : size.width / 2 + 8
    return [0.5, 0.35, 0.65, 0.2, 0.8].flatMap((fraction) => {
      const anchor = { x: from.x + dx * fraction, y: from.y + dy * fraction }
      return [0, 28, 56].flatMap((extraOffset) => [-1, 1].map((side) => {
        const offset = baseOffset + extraOffset
        const distanceCost = extraOffset * 2 + Math.abs(fraction - 0.5) * 40
        return horizontal
          ? { x: anchor.x, y: anchor.y + side * offset, anchor, length, distanceCost }
          : { x: anchor.x + side * offset, y: anchor.y, anchor, length, distanceCost }
      }))
    })
  })
}

function placementRect(position: GraphPoint, size: LabelSize, margin = 0): Rect {
  return {
    left: position.x - size.width / 2 - margin,
    right: position.x + size.width / 2 + margin,
    top: position.y - size.height / 2 - margin,
    bottom: position.y + size.height / 2 + margin,
  }
}

function overlaps(left: Rect, right: Rect) {
  return left.left < right.right && left.right > right.left && left.top < right.bottom && left.bottom > right.top
}

export function layoutEdgeLabels(edges: LabelEdge[], nodes: LabelNode[]) {
  const nodeRects = nodes.map((node) => ({ left: node.x - 6, right: node.x + node.width + 6, top: node.y - 6, bottom: node.y + node.height + 6 }))
  const occupied: Rect[] = []
  const placements = new Map<string, EdgeLabelPlacement>()

  edges.forEach((edge) => {
    const candidates = placementCandidates(edge.points, edge.size)
    const ranked = candidates.map((candidate) => {
      const rect = placementRect(candidate, edge.size, 4)
      const nodeHits = nodeRects.filter((node) => overlaps(rect, node)).length
      const labelHits = occupied.filter((label) => overlaps(rect, label)).length
      return { candidate, rect, score: nodeHits * 10_000 + labelHits * 1_000 + candidate.distanceCost - candidate.length }
    }).sort((left, right) => left.score - right.score)
    const selected = ranked[0]
    if (!selected) return
    placements.set(edge.id, selected.candidate)
    occupied.push(selected.rect)
  })

  return placements
}
